import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getUserDetailsBaseUrl } from '../utils/chatIdentity';

const VehicleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
        <circle cx="7" cy="17" r="2" />
        <path d="M9 17h6" />
        <circle cx="17" cy="17" r="2" />
    </svg>
);

const PlusIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

const getVehicleLabel = (vehicle) => (
    [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ')
);

// Photo slot for the garage cards. No vehicle carries a picture yet; when one does, any of
// these fields is shown in place of the silhouette.
const getVehicleImage = (vehicle) => (
    vehicle?.image_url || vehicle?.photo_url || vehicle?.vehicle_image_url || vehicle?.image || ''
);

const CarSilhouette = () => (
    <svg viewBox="0 0 120 50" className="h-auto w-[62%] max-w-[190px] drop-shadow-[0_10px_14px_rgba(0,0,0,0.45)] transition-transform duration-500 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.04]" aria-hidden="true">
        <path d="M8 34c0-4 2-6 6-7l16-3 12-10c2-2 5-3 8-3h26c4 0 7 1 10 4l10 9 10 2c5 1 8 4 8 8v3c0 1-1 2-2 2h-8a10 10 0 0 0-20 0H38a10 10 0 0 0-20 0h-8c-1 0-2-1-2-2z" fill="#F2F2F2" />
        <path d="M46 15l-6 9h22V14H52c-3 0-5 0-6 1zM66 14v10h24l-8-8c-2-1.5-4-2-7-2z" fill="#414141" opacity="0.85" />
        <path d="M108 30h4" stroke="#D81F26" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="28" cy="39" r="7.5" fill="#1F1F1F" stroke="#8C8C8C" strokeWidth="1.5" />
        <circle cx="28" cy="39" r="2.6" fill="#B3B3B3" />
        <circle cx="94" cy="39" r="7.5" fill="#1F1F1F" stroke="#8C8C8C" strokeWidth="1.5" />
        <circle cx="94" cy="39" r="2.6" fill="#B3B3B3" />
    </svg>
);

// Preview picture: the vehicle photo when available, otherwise a silhouette on a charcoal
// garage floor. The active vehicle gets a red spotlight from above.
const VehicleBay = ({ vehicle, isSelected }) => {
    const [failed, setFailed] = useState(false);
    const src = getVehicleImage(vehicle);
    const showPhoto = Boolean(src) && !failed;

    useEffect(() => {
        setFailed(false);
    }, [src]);

    return (
        <div
            className={`relative flex aspect-[16/7] items-end justify-center overflow-hidden rounded-2xl ${
                isSelected
                    ? 'bg-[radial-gradient(ellipse_60%_70%_at_50%_0%,rgba(216,31,38,0.35),transparent_70%),linear-gradient(160deg,#2A2A2A,#1F1F1F)]'
                    : 'bg-[linear-gradient(160deg,#333333,#1F1F1F)]'
            }`}
        >
            {showPhoto ? (
                <img
                    src={src}
                    alt={getVehicleLabel(vehicle)}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={() => setFailed(true)}
                    draggable={false}
                />
            ) : (
                <>
                    {/* Floor line and contact shadow make the silhouette sit in a bay. */}
                    <span className="absolute inset-x-0 bottom-[22%] h-px bg-white/10" aria-hidden="true" />
                    <span className="absolute bottom-[16%] left-1/2 h-3 w-1/2 -translate-x-1/2 rounded-[50%] bg-black/50 blur-md" aria-hidden="true" />
                    <div className="relative mb-[13%] flex w-full justify-center">
                        <CarSilhouette />
                    </div>
                </>
            )}
            {isSelected && (
                <span className="absolute left-3 top-3 rounded-full bg-[#D81F26] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-[0_4px_10px_rgba(216,31,38,0.4)]">
                    Active
                </span>
            )}
        </div>
    );
};

const formatSelectorMileage = (vehicle) => {
    const raw = vehicle?.odometer ?? vehicle?.total_distance_miles ?? vehicle?.mileage ?? vehicle?.miles;
    if (raw === null || raw === undefined || raw === '') return '';
    const number = typeof raw === 'number' ? raw : Number(String(raw).replace(/[^\d.]/g, ''));
    return Number.isFinite(number) && number > 0 ? `${Math.round(number).toLocaleString('en-US')} mi` : '';
};

// Only the details a vehicle actually has are listed.
const getVehicleDetails = (vehicle) => [
    ['Year', vehicle?.year],
    ['Make', vehicle?.make],
    ['Model', vehicle?.model],
    ['Trim', vehicle?.trim],
    ['Mileage', formatSelectorMileage(vehicle)],
    ['Engine', vehicle?.engine],
    ['Transmission', vehicle?.transmission],
    ['Fuel', vehicle?.fuel_type],
    ['Drivetrain', vehicle?.drivetrain],
    ['Plate', vehicle?.plate_number],
    ['VIN', vehicle?.vin],
].filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '');

const VehicleSelectorSkeleton = ({ variant = 'floating', collapsed = false }) => {
    const isSidebar = variant === 'sidebar';
    const isMobileTop = variant === 'mobileTop';

    return (
        <div
            aria-hidden="true"
            className={isSidebar
                ? `${collapsed ? 'h-10 w-10 justify-center px-0' : 'w-full gap-3 px-4'} flex items-center py-2.5 rounded-xl bg-white border border-[#d9d9d9] shadow-sm`
                : isMobileTop
                    ? 'flex h-10 w-full min-w-0 items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white/65 px-2.5 shadow-sm backdrop-blur-md'
                : 'flex items-center gap-2 px-5 py-2.5 bg-white/90 backdrop-blur-md border border-[#d9d9d9] rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.08)]'
            }
        >
            <div className={`${isSidebar ? 'h-5 w-5' : isMobileTop ? 'h-5 w-5 rounded-full' : 'h-6 w-6 rounded-full'} shrink-0 animate-pulse bg-[#d9d9d9]`} />
            {!collapsed && (
                <>
                    <div className="h-4 min-w-0 flex-1 animate-pulse rounded bg-[#d9d9d9]" />
                    <div className="h-3 w-3 shrink-0 animate-pulse rounded bg-[#d9d9d9]" />
                </>
            )}
        </div>
    );
};

const VehicleSelector = ({ authState, variant = 'floating', collapsed = false, onNavigate }) => {
    const [vehicles, setVehicles] = useState([]);
    const [activeVehicleId, setActiveVehicleId] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [previewId, setPreviewId] = useState(null);
    const [isMobileListOpen, setIsMobileListOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const selectorRef = useRef(null);
    const dialogRef = useRef(null);
    const listRef = useRef(null);
    const navigate = useNavigate();
    const isSidebar = variant === 'sidebar';
    const isMobileTop = variant === 'mobileTop';

    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedTrigger = selectorRef.current?.contains(event.target);
            const clickedDialog = dialogRef.current?.contains(event.target);
            if (!clickedTrigger && !clickedDialog) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Keep the previewed card visible in the list (the phone strip scrolls sideways).
    useEffect(() => {
        if (!isOpen) return;
        listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }, [isOpen, previewId, isMobileListOpen]);

    useEffect(() => {
        if (authState?.status !== 'authenticated' || !authState.identity) {
            setVehicles([]);
            setIsLoading(false);
            return;
        }

        let isMounted = true;
        const fetchVehicles = async () => {
            setIsLoading(true);
            try {
                const userDetailsBaseUrl = getUserDetailsBaseUrl();
                const response = await fetch(`${userDetailsBaseUrl}/api/lookup_user_data?phone_number=${encodeURIComponent(authState.identity)}&query=vehicle_worklist`);
                if (!response.ok) throw new Error('Failed to fetch');
                const data = await response.json();
                
                if (isMounted && data.result) {
                    setVehicles(data.result.vehicles || []);
                    setActiveVehicleId(data.result.active_vehicle_id || null);
                }
            } catch (err) {
                console.error('Failed to fetch vehicles for selector:', err);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchVehicles();
        window.addEventListener('dash_vehicle_updated', fetchVehicles);
        window.addEventListener('dash_vehicle_switched', fetchVehicles);
        return () => {
            isMounted = false;
            window.removeEventListener('dash_vehicle_updated', fetchVehicles);
            window.removeEventListener('dash_vehicle_switched', fetchVehicles);
        };
    }, [authState?.status, authState?.identity]);

    const handleSelect = async (vehicleId) => {
        setIsOpen(false);
        if (vehicleId === activeVehicleId) return;
        
        // Optimistic update
        setActiveVehicleId(vehicleId);

        try {
            const userDetailsBaseUrl = getUserDetailsBaseUrl();
            const response = await fetch(`${userDetailsBaseUrl}/api/vehicle_selection`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    phone_number: authState.identity,
                    vehicle_id: vehicleId
                })
            });
            if (!response.ok) {
                throw new Error('Failed to set active vehicle');
            }
            // Notify other components (e.g. ChatPage placeholder) that the vehicle changed.
            // Carry the new active_vehicle_id so ChatPage's redirect handler moves the
            // conversation to THIS vehicle's chat (existing one, or a fresh chat) — the same
            // behavior as typing "Switch vehicle to [vehicle]". Without this id the redirect
            // handler no-ops and the current chat stays bound to the old vehicle.
            window.dispatchEvent(new CustomEvent('dash_vehicle_switched', {
                detail: { active_vehicle_id: vehicleId, vehicle_id: vehicleId, source: 'vehicle_selector' },
            }));
        } catch (err) {
            console.error('Failed to set active vehicle:', err);
            // If it fails, could revert optimistic update here, but let's keep it simple
        }
    };

    const goToGarage = () => {
        setIsOpen(false);
        if (onNavigate) {
            onNavigate('/garage');
            return;
        }
        navigate('/garage');
    };

    // Open the guided "Add your vehicle" form in chat (handled by ChatLayout,
    // which starts a fresh chat and signals ChatPage), instead of redirecting
    // to the Garage page.
    const addVehicle = () => {
        setIsOpen(false);
        window.dispatchEvent(new CustomEvent('dash_open_add_vehicle'));
    };

    if (authState?.status !== 'authenticated') {
        return null;
    }

    if (isLoading && vehicles.length === 0) {
        return <VehicleSelectorSkeleton variant={variant} collapsed={collapsed} />;
    }

    if (vehicles.length === 0) {
        return (
            <button
                onClick={addVehicle}
                title={collapsed ? 'Add Vehicle' : ''}
                className={isSidebar
                    ? `${collapsed ? 'h-10 w-10 justify-center px-0' : 'w-full gap-3 px-4'} group flex items-center py-2.5 rounded-xl bg-white border border-[#d9d9d9] hover:border-[#414141] shadow-sm text-[#414141] transition-[background-color,color,box-shadow,border-color,transform] duration-150 ease-out active:scale-[0.98]`
                    : isMobileTop
                        ? 'flex h-10 w-full min-w-0 items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white/65 px-2.5 text-[#414141] shadow-sm backdrop-blur-md transition hover:bg-white/85 active:scale-[0.98]'
                    : 'flex items-center gap-2 px-5 py-2.5 bg-white/90 backdrop-blur-md border border-[#d9d9d9] rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:border-[#414141]'
                }
            >
                {/* 3D lift on hover: perspective + rotateX tips the glyph toward the viewer,
                    rotateZ(90deg) spins it (a "+" lands identical, so the motion reads as
                    animation without changing the symbol), and the coloured drop-shadow
                    beneath sells the height. Kirusa accent here; New Chat uses violet. */}
                <div className={`${isSidebar ? 'w-5 h-5 text-[#D81F26] group-hover:text-[#D81F26] group-hover:[transform:perspective(320px)_rotateX(-20deg)_rotateZ(90deg)_scale(1.35)] group-hover:drop-shadow-[0_4px_7px_rgba(216,31,38,0.55)] transition-[transform,filter,color] duration-300 ease-out' : isMobileTop ? 'h-5 w-5 rounded-full bg-[#FDEDEE] text-[#D81F26]' : 'w-6 h-6 bg-[#FDEDEE] text-[#D81F26] rounded-full'} flex-shrink-0 flex items-center justify-center`}>
                    <PlusIcon />
                </div>
                {!collapsed && <span className={`${isMobileTop ? 'text-[11px]' : 'text-[13px]'} font-black text-black tracking-tight truncate`}>
                    Add Vehicle
                </span>}
            </button>
        );
    }

    const activeVehicle = vehicles.find(v => v.vehicle_id === activeVehicleId) || vehicles[0];

    const displayLabel = getVehicleLabel(activeVehicle);
    const previewVehicle = vehicles.find(v => v.vehicle_id === previewId) || activeVehicle;
    const previewIsActive = previewVehicle.vehicle_id === activeVehicle.vehicle_id;
    const previewDetails = getVehicleDetails(previewVehicle);

    // Arrow keys move the preview through the list; Enter on an item still previews it.
    const handleListKeyDown = (event) => {
        const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const currentIndex = vehicles.findIndex(v => v.vehicle_id === previewVehicle.vehicle_id);
        const next = vehicles[(currentIndex + step + vehicles.length) % vehicles.length];
        setPreviewId(next.vehicle_id);
        event.currentTarget.querySelectorAll('[role="option"]')[vehicles.indexOf(next)]?.focus();
    };

    const vehicleDialog = isOpen && typeof document !== 'undefined'
        ? createPortal(
            <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-8 md:pt-14">
                <div
                    className="absolute inset-0 bg-black/35 backdrop-blur-md animate-in fade-in duration-150"
                    onMouseDown={() => setIsOpen(false)}
                />
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-label="Select vehicle"
                    aria-modal="true"
                    onMouseDown={(event) => event.stopPropagation()}
                    className="relative z-[101] w-full max-w-[680px] overflow-hidden rounded-[26px] border border-[#d9d9d9] bg-white shadow-[0_24px_80px_rgba(45,45,45,0.24)] animate-in fade-in slide-in-from-top-4 zoom-in-95 duration-200"
                >
                    <div className="flex items-start justify-between gap-4 border-b border-[#E6E6E6] bg-white px-5 py-4">
                        <div className="min-w-0">
                            <p className="flex items-center gap-2 text-[12px] font-black uppercase tracking-[0.16em] text-[#D81F26]">Select Vehicle<span className="rounded-full bg-[#F2F2F2] px-2 py-0.5 text-[10px] tracking-[0.08em] text-[#6B6B6B]">{vehicles.length} in garage</span></p>
                            <p className="mt-1 truncate text-lg font-black text-[#1F1F1F]">{displayLabel}</p>
                            <p className="mt-1 text-xs font-semibold text-black/45">CarFido will use the selected vehicle for chat, diagnostics, and reports.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black/35 hover:text-black border border-black/5 shadow-sm"
                            aria-label="Close vehicle selector"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex max-h-[calc(100dvh-230px)] min-h-0 flex-col sm:max-h-[min(68vh,540px)] sm:flex-row">
                        {/* Phones: the list is a dropdown that floats over the preview, so opening it
                            never moves the page. From sm up it is a static column on the left. */}
                        <div className="relative shrink-0 bg-[#F9F9F9] sm:flex sm:min-h-0 sm:w-[250px] sm:flex-col sm:border-r sm:border-[#E6E6E6]">
                        <div className="p-3 sm:hidden">
                            <button
                                type="button"
                                onClick={() => setIsMobileListOpen((open) => !open)}
                                aria-expanded={isMobileListOpen}
                                aria-controls="vehicle-selector-list"
                                className="flex w-full items-center gap-2.5 rounded-xl border border-[#D81F26] bg-white px-2.5 py-2 text-left shadow-[0_6px_16px_rgba(216,31,38,0.14)] transition active:scale-[0.99]"
                            >
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1F1F1F] text-white">
                                    <VehicleIcon />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-[13px] font-black text-[#1F1F1F]">{getVehicleLabel(previewVehicle)}</span>
                                    <span className="mt-0.5 block text-[11px] font-bold text-[#8C8C8C]">
                                        {isMobileListOpen ? 'Choose a vehicle' : `${vehicles.length} ${vehicles.length === 1 ? 'vehicle' : 'vehicles'} · tap to change`}
                                    </span>
                                </span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 text-[#414141] transition-transform duration-200 ${isMobileListOpen ? 'rotate-180' : ''}`} aria-hidden="true">
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </button>
                        </div>
                        <div
                            ref={listRef}
                            id="vehicle-selector-list"
                            role="listbox"
                            aria-label="Your vehicles"
                            onKeyDown={handleListKeyDown}
                            className={`${isMobileListOpen ? 'flex' : 'hidden'} absolute inset-x-3 top-full z-30 -mt-1 max-h-[46vh] flex-col gap-1 overflow-y-auto rounded-2xl border border-[#E6E6E6] bg-white p-2 shadow-[0_18px_40px_rgba(0,0,0,0.18)] animate-in fade-in slide-in-from-top-2 duration-150 sm:static sm:inset-auto sm:mt-0 sm:flex sm:max-h-none sm:min-h-0 sm:flex-1 sm:gap-2 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-3 sm:shadow-none sm:animate-none`}
                        >
                            {vehicles.map((vehicle, index) => {
                                const isActive = vehicle.vehicle_id === activeVehicleId;
                                const isPreviewed = vehicle.vehicle_id === previewVehicle.vehicle_id;
                                return (
                                    <button
                                        key={vehicle.vehicle_id}
                                        type="button"
                                        role="option"
                                        aria-selected={isPreviewed}
                                        tabIndex={isPreviewed ? 0 : -1}
                                        onClick={() => {
                                            setPreviewId(vehicle.vehicle_id);
                                            setIsMobileListOpen(false);
                                        }}
                                        onDoubleClick={() => handleSelect(vehicle.vehicle_id)}
                                        style={{ animationDelay: `${Math.min(index * 50, 250)}ms` }}
                                        className={`group animate-dash-intro-rise relative flex w-full shrink-0 items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left outline-none transition-[background-color,border-color,box-shadow] duration-200 focus-visible:ring-2 focus-visible:ring-[#D81F26]/40 sm:w-full ${
                                            isPreviewed
                                                ? 'border-[#D81F26] bg-white shadow-[0_6px_16px_rgba(216,31,38,0.14)]'
                                                : 'border-transparent bg-transparent hover:border-[#d9d9d9] hover:bg-white'
                                        }`}
                                    >
                                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
                                            isPreviewed ? 'bg-[#1F1F1F] text-white' : 'bg-[#E6E6E6] text-[#414141] group-hover:bg-[#1F1F1F] group-hover:text-white'
                                        }`}>
                                            <VehicleIcon />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[13px] font-black text-[#1F1F1F]">{getVehicleLabel(vehicle)}</span>
                                            <span className={`mt-0.5 flex items-center gap-1 text-[11px] font-bold ${isActive ? 'text-[#D81F26]' : 'text-[#8C8C8C]'}`}>
                                                {isActive && <span className="h-1.5 w-1.5 rounded-full bg-[#D81F26]" aria-hidden="true" />}
                                                {isActive ? 'In use' : 'Tap to preview'}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        </div>

                        {/* Re-keyed per vehicle so each preview slides in fresh. Tapping it closes
                            the floating phone list. */}
                        <div
                            key={previewVehicle.vehicle_id}
                            onPointerDown={() => { if (isMobileListOpen) setIsMobileListOpen(false); }}
                            className="flex min-h-0 flex-1 flex-col overflow-y-auto border-t border-[#E6E6E6] animate-dash-slide-in-right sm:border-t-0"
                        >
                            <div className="p-4 pb-0">
                            <VehicleBay vehicle={previewVehicle} isSelected={previewIsActive} />
                            <div className="mt-3 flex items-center gap-2">
                                <h3 className="min-w-0 truncate text-base font-black text-[#1F1F1F]">{getVehicleLabel(previewVehicle)}</h3>
                            </div>
                            <p className={`mt-0.5 text-xs font-semibold ${previewIsActive ? 'text-[#D81F26]' : 'text-[#6B6B6B]'}`}>
                                {previewIsActive ? 'Currently used by CarFido' : 'Switch chat to this vehicle'}
                            </p>
                            {previewDetails.length > 0 && (
                                <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                    {previewDetails.map(([label, value]) => (
                                        <div key={label} className={`min-w-0 rounded-xl border border-[#E6E6E6] bg-[#F9F9F9] px-3 py-1.5 ${label === 'VIN' ? 'col-span-2 sm:col-span-3' : ''}`}>
                                            <dt className="text-[10px] font-black uppercase tracking-[0.12em] text-[#8C8C8C]">{label}</dt>
                                            <dd className="mt-0.5 truncate text-[13px] font-bold text-[#1F1F1F]" title={String(value)}>{value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                            </div>
                            {/* Pinned so the confirm action stays reachable however many details are listed. */}
                            <div className="sticky bottom-0 mt-auto bg-gradient-to-t from-white via-white to-white/0 px-4 pb-4 pt-4">
                            <button
                                type="button"
                                onClick={() => handleSelect(previewVehicle.vehicle_id)}
                                disabled={previewIsActive}
                                className={`flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-black transition active:scale-[0.99] ${
                                    previewIsActive
                                        ? 'cursor-default border border-[#F5C2C4] bg-[#FDEDEE] text-[#D81F26]'
                                        : 'bg-[#1F1F1F] text-white shadow-sm hover:bg-[#D81F26]'
                                }`}
                            >
                                {previewIsActive ? <><CheckIcon /> Currently in use</> : 'Use this vehicle'}
                            </button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 border-t border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4 sm:grid-cols-2">
                        <button
                            type="button"
                            onClick={goToGarage}
                            className="flex min-h-[44px] items-center justify-center rounded-xl border border-black/10 bg-white px-4 text-sm font-black text-[#282828] shadow-sm hover:bg-[#F9F9F9]"
                        >
                            Manage in Garage
                        </button>
                        <button
                            type="button"
                            onClick={addVehicle}
                            className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#D81F26] px-4 text-sm font-black text-white shadow-sm hover:bg-[#B8181E]"
                        >
                            <PlusIcon />
                            Add Vehicle
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )
        : null;

    return (
        <div className="relative w-full" ref={selectorRef}>
            <button
                onClick={() => {
                    if (!isOpen) {
                        setPreviewId(activeVehicleId);
                        setIsMobileListOpen(false);
                    }
                    setIsOpen(!isOpen);
                }}
                title={collapsed ? displayLabel : ''}
                className={isSidebar
                    ? `${collapsed ? 'h-10 w-10 justify-center px-0' : 'w-full gap-3 px-4'} flex items-center py-2.5 rounded-xl bg-white border border-[#d9d9d9] hover:border-[#414141] shadow-sm text-[#414141] transition-[background-color,color,box-shadow,transform] duration-150 ease-out active:scale-[0.98]`
                    : isMobileTop
                        ? 'flex h-10 w-full min-w-0 items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white/65 px-2.5 text-[#414141] shadow-sm backdrop-blur-md transition hover:bg-white/85 active:scale-[0.98]'
                    : 'flex items-center gap-2 px-5 py-2.5 bg-white/90 backdrop-blur-md border border-[#d9d9d9] rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:border-[#414141]'
                }
                aria-haspopup="dialog"
                aria-expanded={isOpen}
            >
                <div className={`${isSidebar ? 'w-5 h-5 text-[#414141]' : isMobileTop ? 'h-5 w-5 rounded-full bg-[#F2F2F2] text-[#414141]' : 'w-6 h-6 bg-[#F2F2F2] text-[#414141] rounded-full'} flex-shrink-0 flex items-center justify-center`}>
                    <VehicleIcon />
                </div>
                {!collapsed && (
                    <span className={`flex-1 min-w-0 text-left ${isMobileTop ? 'text-[11px]' : 'text-[13px]'} font-black text-black tracking-tight truncate`}>
                        {displayLabel}
                    </span>
                )}
                {!collapsed && (
                    <svg width={isMobileTop ? '12' : '14'} height={isMobileTop ? '12' : '14'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`flex-shrink-0 text-black/30 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                        <path d="M6 9l6 6 6-6"/>
                    </svg>
                )}
            </button>

            {vehicleDialog}
        </div>
    );
};

export default VehicleSelector;
